import React, { useState, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, FolderRoot as Football, Calendar, Clock, MapPin, DollarSign, X } from 'lucide-react';
import { useSupabase } from '../contexts/SupabaseContext';
import { useAuth } from '../hooks/useAuth';
import { usePlanFeatures } from '../hooks/usePlanFeatures';

interface GameDay {
  id: string;
  dia: string;
  horario: string;
  local: string;
  valor: number;
}

const CreateGroupPage: React.FC = () => {
  const { supabase } = useSupabase();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { canCreateGroup } = usePlanFeatures();

  const [nome, setNome] = useState('');
  const [tipo, setTipo] = useState('society');
  const [mensalidade, setMensalidade] = useState('');
  const [cashBalance, setCashBalance] = useState('');
  const [gameDays, setGameDays] = useState<GameDay[]>([]);
  
  // PIX configuration states
  const [pixKey, setPixKey] = useState('');
  const [pixKeyType, setPixKeyType] = useState<'cpf' | 'cnpj' | 'email' | 'phone'>('cpf');
  const [pixHolderName, setPixHolderName] = useState('');
  const [pixWhatsApp, setPixWhatsApp] = useState('');
  const [pixWhatsAppCountryCode, setPixWhatsAppCountryCode] = useState('+55');

  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleAddGameDay = () => {
    setGameDays([...gameDays, {
      id: crypto.randomUUID(),
      dia: '',
      horario: '',
      local: '',
      valor: 0
    }]);
  };

  const handleRemoveGameDay = (index: number) => {
    setGameDays(gameDays.filter((_, i) => i !== index));
  };

  const handleGameDayChange = (index: number, field: keyof GameDay, value: string | number) => {
    const updated = [...gameDays];
    updated[index] = { ...updated[index], [field]: value };
    setGameDays(updated);
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!nome || !tipo) {
      setError('Por favor, preencha os campos obrigatórios');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { data: gruposExistentes, error: gruposError } = await supabase
        .from('grupos')
        .select('id')
        .eq('criado_por', user!.id);

      if (gruposError) throw gruposError;

      const { allowed, message } = await canCreateGroup(gruposExistentes?.length || 0);
      if (!allowed) {
        setError(message || 'Você atingiu o limite de grupos permitidos no seu plano.');
        setLoading(false);
        return;
      }

      const { data: grupo, error: insertError } = await supabase
        .from('grupos')
        .insert({ nome: nome.trim(), tipo, criado_por: user!.id })
        .select()
        .single();

      if (insertError) throw insertError;

      if (grupo) {
        // Gerar token público se PIX estiver configurado
        let pixPublicToken = null;
        if (pixKey.trim() && pixHolderName.trim()) {
          pixPublicToken = crypto.randomUUID();
        }
        
        await supabase
          .from('grupo_configuracoes')
          .insert({
            grupo_id: grupo.id,
            valor_padrao: mensalidade ? parseFloat(mensalidade) : null,
            caixa_atual: cashBalance ? parseFloat(cashBalance) : 0,
            pix_key: pixKey.trim() || null,
            pix_key_type: pixKeyType,
            pix_holder_name: pixHolderName.trim() || null,
            pix_whatsapp: pixWhatsApp.trim() || null,
            pix_whatsapp_country_code: pixWhatsAppCountryCode,
            pix_public_token: pixPublicToken
          });

        if (gameDays.length > 0) {
          await supabase
            .from('grupo_dias_jogo')
            .insert(
              gameDays.map(day => ({
                grupo_id: grupo.id,
                dia: day.dia,
                horario: day.horario,
                local: day.local,
                valor: day.valor
              }))
            );
        }

        await supabase
          .from('profiles')
          .update({
            grupo_ativo: grupo.id,
            atualizado_em: new Date().toISOString()
          })
          .eq('id', user!.id);

        navigate('/jogadores');
      }
    } catch (err) {
      console.error('Erro ao criar grupo:', err);
      setError('Erro ao criar o grupo');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <header className="mb-8">
        <h1 className="text-2xl font-bold mb-2">Criar Novo Grupo</h1>
        <p className="text-gray-500 dark:text-gray-400">
          Preencha as informações para criar seu grupo
        </p>
      </header>

      <div className="max-w-2xl">
        <div className="card p-6">
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 p-3 rounded-lg mb-4 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label htmlFor="nome" className="block text-sm font-medium mb-1">Nome do Grupo *</label>
              <input id="nome" type="text" className="input" value={nome} onChange={(e) => setNome(e.target.value)} required />
            </div>

            <div className="mb-4">
              <label htmlFor="tipo" className="block text-sm font-medium mb-1">Tipo de Campo *</label>
              <select id="tipo" className="input" value={tipo} onChange={(e) => setTipo(e.target.value)} required>
                <option value="society">Society</option>
                <option value="campo">Campo</option>
                <option value="quadra">Quadra</option>
              </select>
            </div>

            <div className="mb-4">
              <label htmlFor="mensalidade" className="block text-sm font-medium mb-1">Valor da Mensalidade *</label>
              <div className="relative">
                <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-500">R$</span>
                <input id="mensalidade" type="number" step="0.01" min="0" className="input pl-10" value={mensalidade} onChange={(e) => setMensalidade(e.target.value)} required />
              </div>
            </div>

            <div className="mb-4">
              <label htmlFor="caixa" className="block text-sm font-medium mb-1">Dinheiro em Caixa *</label>
              <div className="relative">
                <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-500">R$</span>
                <input id="caixa" type="number" step="0.01" min="0" className="input pl-10" value={cashBalance} onChange={(e) => setCashBalance(e.target.value)} required />
              </div>
            </div>

            <div className="mb-6">
              <h3 className="text-lg font-medium mb-4">Configuração PIX</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                Configure sua chave PIX para facilitar o recebimento das mensalidades. 
                Um QR Code e link de pagamento serão gerados automaticamente.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="pixKeyType" className="block text-sm font-medium mb-1">
                    Tipo de Chave PIX
                  </label>
                  <select
                    id="pixKeyType"
                    className="input"
                    value={pixKeyType}
                    onChange={(e) => setPixKeyType(e.target.value as any)}
                  >
                    <option value="cpf">CPF</option>
                    <option value="cnpj">CNPJ</option>
                    <option value="email">E-mail</option>
                    <option value="phone">Telefone</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="pixKey" className="block text-sm font-medium mb-1">
                    Chave PIX
                  </label>
                  <input
                    type="text"
                    id="pixKey"
                    className="input"
                    value={pixKey}
                    onChange={(e) => setPixKey(e.target.value)}
                    placeholder={
                      pixKeyType === 'cpf' ? '000.000.000-00' :
                      pixKeyType === 'cnpj' ? '00.000.000/0000-00' :
                      pixKeyType === 'email' ? 'email@exemplo.com' :
                      '(00) 00000-0000'
                    }
                  />
                </div>

                <div className="md:col-span-2">
                  <label htmlFor="pixHolderName" className="block text-sm font-medium mb-1">
                    Nome do Titular
                  </label>
                  <input
                    type="text"
                    id="pixHolderName"
                    className="input"
                    value={pixHolderName}
                    onChange={(e) => setPixHolderName(e.target.value)}
                    placeholder="Nome completo do titular da conta PIX"
                  />
                </div>

                <div className="md:col-span-2">
                  <label htmlFor="pixWhatsApp" className="block text-sm font-medium mb-1">
                    WhatsApp para Comprovantes
                  </label>
                  <div className="flex gap-2">
                    <select
                      className="input w-32"
                      value={pixWhatsAppCountryCode}
                      onChange={(e) => setPixWhatsAppCountryCode(e.target.value)}
                    >
                      <option value="+55">🇧🇷 +55</option>
                      <option value="+1">🇺🇸 +1</option>
                      <option value="+44">🇬🇧 +44</option>
                      <option value="+33">🇫🇷 +33</option>
                      <option value="+49">🇩🇪 +49</option>
                      <option value="+39">🇮🇹 +39</option>
                      <option value="+34">🇪🇸 +34</option>
                      <option value="+31">🇳🇱 +31</option>
                      <option value="+32">🇧🇪 +32</option>
                      <option value="+41">🇨🇭 +41</option>
                      <option value="+43">🇦🇹 +43</option>
                      <option value="+351">🇵🇹 +351</option>
                    </select>
                    <input
                      type="tel"
                      id="pixWhatsApp"
                      className="input flex-1"
                      value={pixWhatsApp}
                      onChange={(e) => {
                        // Format phone number based on country code
                        const numbers = e.target.value.replace(/\D/g, '');
                        let formatted = numbers;
                        
                        if (pixWhatsAppCountryCode === '+55') {
                          // Brazilian format: (11) 99999-9999
                          if (numbers.length <= 11) {
                            formatted = numbers
                              .replace(/(\d{2})(\d)/, '($1) $2')
                              .replace(/(\d{4,5})(\d{4})$/, '$1-$2');
                          }
                        } else if (pixWhatsAppCountryCode === '+1') {
                          // US format: (555) 123-4567
                          if (numbers.length <= 10) {
                            formatted = numbers
                              .replace(/(\d{3})(\d)/, '($1) $2')
                              .replace(/(\d{3})(\d{4})$/, '$1-$2');
                          }
                        } else {
                          // European format: simple grouping
                          if (numbers.length <= 12) {
                            formatted = numbers
                              .replace(/(\d{2,3})(\d{3})(\d{3,4})$/, '$1 $2 $3');
                          }
                        }
                        
                        setPixWhatsApp(formatted);
                      }}
                      placeholder={
                        pixWhatsAppCountryCode === '+55' ? '(11) 99999-9999' :
                        pixWhatsAppCountryCode === '+1' ? '(555) 123-4567' :
                        '12 345 6789'
                      }
                    />
                  </div>
                  <p className="text-sm text-gray-500 mt-1">
                    Número completo com código do país para onde os jogadores devem enviar os comprovantes PIX.
                  </p>
                </div>
              </div>
            </div>

            <div className="mb-6">
              <div className="flex justify-between items-center mb-2">
                <h3 className="text-sm font-medium">Dias de Jogo</h3>
                <button type="button" onClick={handleAddGameDay} className="btn-outline text-sm">
                  <Plus className="w-4 h-4 mr-1" /> Adicionar Dia
                </button>
              </div>

              {gameDays.map((day, index) => (
                <div key={day.id} className="border p-4 rounded-lg mb-4 space-y-3 relative">
                  <button type="button" onClick={() => handleRemoveGameDay(index)} className="absolute top-2 right-2 text-red-500 hover:text-red-700">
                    <X className="w-4 h-4" />
                  </button>

                  <div>
                    <label className="block text-sm font-medium mb-1">Dia da Semana</label>
                    <select className="input" value={day.dia} onChange={(e) => handleGameDayChange(index, 'dia', e.target.value)} required>
                      <option value="">Selecione</option>
                      <option value="domingo">Domingo</option>
                      <option value="segunda">Segunda-feira</option>
                      <option value="terca">Terça-feira</option>
                      <option value="quarta">Quarta-feira</option>
                      <option value="quinta">Quinta-feira</option>
                      <option value="sexta">Sexta-feira</option>
                      <option value="sabado">Sábado</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Horário</label>
                    <input type="time" className="input" value={day.horario} onChange={(e) => handleGameDayChange(index, 'horario', e.target.value)} />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Local</label>
                    <input type="text" className="input" placeholder="Nome ou endereço" value={day.local} onChange={(e) => handleGameDayChange(index, 'local', e.target.value)} />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Valor</label>
                    <div className="relative">
                      <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-500">R$</span>
                      <input type="number" step="0.01" min="0" className="input pl-10" value={day.valor} onChange={(e) => handleGameDayChange(index, 'valor', parseFloat(e.target.value))} />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <button type="submit" className="btn-primary w-full" disabled={loading}>
              {loading ? 'Criando grupo...' : (<><Plus className="w-4 h-4 mr-2" /> Criar Grupo</>)}
            </button>
          </form>
        </div>

        <div className="mt-8 text-center">
          <div className="inline-flex items-center justify-center p-4 rounded-full bg-primary-100 dark:bg-primary-900/20">
            <Football className="w-6 h-6 text-primary-500" />
          </div>
          <h2 className="text-lg font-semibold mt-4 mb-2">Comece a organizar seu time</h2>
          <p className="text-gray-500 dark:text-gray-400 max-w-md mx-auto">
            Após criar o grupo, você poderá adicionar jogadores, gerenciar finanças e muito mais.
          </p>
        </div>
      </div>
    </div>
  );
};

export default CreateGroupPage;
